import { createHash } from 'crypto';
import $ from 'jquery';
import _ from 'underscore';
import { initAutosubmit } from 'util/autosubmit';
import {
  appVersion,
  isAndroid,
  isBrilliantPremium,
  isIos,
  isMobile,
  jqOffOn,
  $window,
} from 'util/global';
import * as latex from 'util/latex';
import { initGenericControllers } from 'app/generic_controllers';
import preloaders from 'app/preloaders';
import { practiceAnon } from 'app/solvables';

/** @module */
const ir = {};

const irContext = {};

let mostRecentContext = null;

let sha1 = null;

/**
 * Store previous num free solutions to prevent this number from increasing
 * @type {Number}
 */
var savedNumFreeSolutionsRemaining = null;

/**
 * Check that the answer is a number and has not already been entered for this problem
 * @param {Object} data - solvable data object
 * @return {Object} updated data object
 */
var checkAnswerValidity = function (data) {
  if (data.interactive) {
    data.validationError = false;
    data.alreadyTriedThisAnswer = false;
    return data;
  }

  // if answer starts with plus, remove it
  if (data.answer.indexOf('+') === 0) {
    data.answer = data.answer.substring(1);
  }

  // strip 0s from beginning of answer
  if (data.answer.length) {
    data.answer = data.answer.replace(/^0+/, '');
    if (data.answer === '') {
      data.answer = '0';
    }
  }

  // non-mcq answers should be int or float
  var numberAnswer = parseFloat(data.answer),
    answerDisallowedChars = data.answer.replace(/[\d-Ee\.]/g, '');

  // answer isn't a number or is using disallowed chars
  if (isNaN(numberAnswer) || answerDisallowedChars.length > 0) {
    data.validationError = true;
  } else {
    // user's answer is float, but looking for integer answer
    if (data.answer.match(/[\.Ee]/) !== null && !data.isFloat) {
      data.validationError = true;

      // answer is ok
    } else {
      data.validationError = false;
    }
  }

  // check that answer isn't in answers list
  data.alreadyTriedThisAnswer = data.answersList.indexOf(data.answer.toString()) >= 0;

  return data;
};

/**
 * Check if the answer is correct. For float answers, prevent instant-updating if answer is
 * incorrect. Float answers can be correct but will validate as incorrect if the answer has
 * any differences in precision or format.
 * @param {Object} data - solvable data object
 * @return {Object} updated data object
 */
var checkAnswerCorrectness = function (data) {
  if (data.interactive) {
    data.answerIsCorrect = $('[name=mark_correct]').prop('checked');
  } else {
    // get answer hash
    var newAnswerHash = sha1.update(data.answer + data.salt).digest('hex');
    data.answerIsCorrect = newAnswerHash == data.hash;

    // handle incorrect answer
    if (!data.answerIsCorrect) {
      // add answer to the front of the array
      data.answersList.unshift(data.answer);
      // only update float answer instantly if answer is 100% correct
      data.preventUpdate = data.isFloat;
    }
    data.triesLeft--;
    data.attemptNumber++;
  }
  return data;
};

/**
 * Process solvable data for an attempt
 * @param {Object} data - solvable data object
 * @return {Object} updated data object
 */
var processAttemptData = function (data) {
  data = data || {};
  data.answer = data.answer.trim();

  data.preventUpdate = false;

  // check if answer is valid
  if (!data.hasMultipleOptions) {
    data = checkAnswerValidity(data);
  }

  // check answer for correctness, only if passed validation
  if (!data.validationError && !data.alreadyTriedThisAnswer) {
    data = checkAnswerCorrectness(data);
  }

  data.canStillAnswer = data.triesLeft > 0 && !data.answerIsCorrect;
  data.isMobile = isMobile;
  data.b2 = isBrilliantPremium;

  // much cleaner to handle this here than in the template
  data.answersListWithAnd = function () {
    var result = data.answersList,
      l = data.answersList.length;

    // [1] => 1
    if (l === 1) {
      result = result[0];

      // [1,2] => 1 and 2
    } else if (l === 2) {
      result = result.join(' and ');

      // [1,2,3] => 1, 2, and 3
    } else {
      result[l - 1] = 'and ' + result[l - 1];
      result = result.join(',');
    }

    return result;
  };

  return data;
};
ir.processAttemptData = processAttemptData;

/**
 * Attach events to new elements rendered from the instant-response template
 * @param {JQuery} $sidebar - solvable details sidebar container
 * @param {Object} data - solvable data object
 */
var instantResponseEvents = function ($sidebar, data) {
  // add class for end-state
  if (!data.canStillAnswer) {
    var newClasses = 'endstate ' + (data.answerIsCorrect ? 'correct' : 'incorrect');
    if (data.isPracticeSection) {
      if (isIos && appVersion.isGreaterThanOrEqualTo('0.9.1')) {
        if (data.answerIsCorrect) {
          window.location = 'answercorrect://';
        } else {
          window.location = 'answerincorrect://';
        }
      } else if (isAndroid) {
        try {
          Android.onPracticeSolvableAnswered(data.answerIsCorrect);
        } catch (e) {
          // NOP
        }
      }
    }
    $sidebar.addClass(newClasses);
  }

  var $solvable = $sidebar.parents('.solv-main');

  // replace unrendered choices with previously rendered (svg) choices to avoid having to
  // load mathjax
  if (data.hasMultipleOptions && data.$choices) {
    $sidebar.find('.btn-mcq').each(function (index) {
      var $choice = $(data.$choices[index]);
      var $thisChoice = $(this);
      var $choiceText = $thisChoice.find('.choice-text');
      var $target = $choiceText.length ? $choiceText : $thisChoice;
      $choice.find('input').remove();
      $target.html($choice.html());
    });
  }

  // disable solution buttons
  $('.btn-solution').not('.solution-btn-b2').addClass('disabled dim').attr('disabled', 'disabled');

  jqOffOn($sidebar.find('.btn.disabled'), 'click', e => {
    e.preventDefault();
  });

  // logged-out practice problems
  practiceAnon($solvable);

  // generic controllers
  initGenericControllers($sidebar);

  // render math in mcq options
  latex.updateMathjax($sidebar);

  // indicate correct and/or incorrect choices
  if (data.problemType == 'single_select') {
    $sidebar.find('.btn-mcq').each(function () {
      var $this = $(this),
        choice = $this.data('choice');

      if (data.answer === choice) {
        $this.addClass(data.answerIsCorrect ? 'correct' : 'incorrect');
      }
    });
  }

  if (data.isPracticeSection || data.isProblemOfTheDay) {
    var $preloader = $solvable.parents('.prac-preload-wrapper');
    if ($preloader.length) {
      preloaders.practice($preloader, {
        reloaded: true,
        instantResponse: true,
      });
    }
  }
};

/**
 * Controller for the instant answer validation and response
 * @param {JQuery} $template - the instant-response template
 */
ir.init = function ($template) {
  var data = $template.data();
  var ctx = irContext[data.solvable] || {};
  var $sidebar = $template.parents('.solv-details,.solv-details-loggedout');
  const formQuery = [
    '#mcq-form',
    '#text_answer_entry_form',
    '#solv-interactive-form',
    '.solv-form-logged-out',
  ];
  var $answerForm = $sidebar.find(formQuery.join(','));

  sha1 = createHash('sha1');

  // remove empty strings from answer list
  data.answersList = data.answersList ? data.answersList.toString() : '';
  try {
    data.answersList = data.answersList.length ? data.answersList.split(/,/g) : [];
  } catch (e) {
    data.answersList = [];
  }
  data.answersList = data.answersList.filter(function (item) {
    return item.length > 0;
  });

  data.answerIsCorrect = false;

  data.$choices =
    data.hasMultipleOptions && $answerForm.find('svg').length ? $answerForm.find('.btn-mcq') : null;

  data.b2 = isBrilliantPremium;

  // num free solutions can only decrease so make sure it isn't increasing from initing
  // something preloaded before the solution was viewed
  if (
    savedNumFreeSolutionsRemaining === null ||
    savedNumFreeSolutionsRemaining >= data.numFreeSolutionsRemaining
  ) {
    savedNumFreeSolutionsRemaining = data.numFreeSolutionsRemaining;
  } else {
    data.numFreeSolutionsRemaining = savedNumFreeSolutionsRemaining;
  }

  // export data for anon users
  if (data.exportOnly) {
    ir.data = data;
    ctx.data = data;
    irContext[data.solvable] = ctx;
    return;
  }

  // the template does not change between requests, so only process it once
  ctx.template = ctx.template || null;
  if (ctx.template === null) {
    ctx.template = _.template($template.text());
  }

  ctx.data = data;
  irContext[data.solvable] = ctx;

  // handle submitting an answer
  $answerForm.on('submit', function (e) {
    e.preventDefault();

    _.defer(function () {
      if ($answerForm.data('no_instant_response')) {
        return;
      }

      mostRecentContext = data.solvable;

      data.viewedSolution = $answerForm.data('viewed-solution');

      if (data.viewedSolution) {
        data.answer = null;
      } else if (data.problemType === 'single_select') {
        data.answer = $answerForm.find('input[type=radio]:checked').val();
      } else if (data.problemType === 'multi_select') {
        const answers = [];
        $answerForm.find('input[type=checkbox]').each(function (index) {
          var $this = $(this);
          if ($this.is(':checked')) {
            answers.push(index + 1);
          }
        });
        data.answer = answers.join('|');
      } else {
        data.answer = $answerForm.find('#id_answer,.logged-out-answer').val();
      }

      if (data.answer || data.interactive) {
        // replace plus
        data.answer = data.answer || '';
        data.answer = data.answer.replace(/__replace_plus__/g, '+');

        data = processAttemptData(data);
      } else {
        // viewed solution. logged out only
        data.canStillAnswer = false;
      }

      var $solvable = $sidebar.parents('.solv-main');

      // save instant response data for verification when the component reloads
      $solvable.data('ir-verification', {
        canStillAnswer: data.canStillAnswer,
        answerIsCorrect: data.answerIsCorrect,
        attemptNumber: data.attemptNumber,
        triesLeft: data.triesLeft,
        answer: data.answer,
        preventUpdate: data.preventUpdate,
      });

      if (!data.preventUpdate) {
        // save the original sidebar
        ctx.$originalSidebar = $sidebar.clone();
        ctx.$solvable = $solvable;

        // render the new sidebar
        _.defer(function () {
          var templateData = data;
          // for mobile web, there is no autosubmit and it is possible to click submit with no answer
          // so canStillAnswer should be set to true for the template
          // but only the template because the backend is not expecting canStillAnswer to be true
          if (data.isMobile && data.hasMultipleOptions && !data.answer) {
            _.extend(templateData, { canStillAnswer: true });
          }
          $sidebar.html(ctx.template(templateData));

          instantResponseEvents($sidebar, data);

          $solvable.trigger($.Event('instantresponse.updated', data));
          // InteractiveProblem.vue needs this event on the window
          $window.trigger($.Event('instantresponse.updated', data));
        });
      }

      ctx.data = data;
      irContext[data.solvable] = ctx;
    });
  });
};

/**
 * Handle request timeouts after rendering instant response template
 */
ir.timeout = function () {
  var ctx = irContext[mostRecentContext],
    $originalSidebar = ctx.$originalSidebar || null;

  if ($originalSidebar !== null) {
    var $sidebar = ctx.$solvable.find('.solv-details');
    $sidebar.replaceWith($originalSidebar);

    $originalSidebar.find('.spinner').removeClass('active');
    $originalSidebar.find(':disabled,.dim').removeClass('dim disabled').removeAttr('disabled');

    _.defer(function () {
      initGenericControllers($originalSidebar);
      initAutosubmit($originalSidebar);
    });
  }
};

module.exports = ir;
